import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createActivity,
  updateActivity,
  getActivityById,
  getActivityTypeOptions,
  getPriorityOptions,
  getStatusOptions,
  getSeasonOptions,
  getRecurrencePatternOptions,
  getTemplates,
  instantiateTemplate,
} from "../../api/planning";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

const ActivityForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = Boolean(
    user?.roles?.some((r) => r === "super_admin" || r?.name === "super_admin")
  );
  const farmTenantParam = useMemo(() => {
    if (!isSuperAdmin) return null;
    const raw = searchParams.get("tenant_id");
    if (raw == null || raw === "") return null;
    const tid = Number(raw);
    return Number.isNaN(tid) ? null : tid;
  }, [isSuperAdmin, searchParams]);

  const { enqueueSnackbar } = useSnackbar();
  const [submissionError, setSubmissionError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      activity_type: "Crop",
      start_date: null,
      end_date: null,
      is_all_day: true,
      season: "Dry Season",
      priority: "Medium",
      status: "Planned",
      is_recurring: false,
      recurrence_pattern: "Daily",
      recurrence_interval: 1,
      recurrence_end_date: null,
      template_id: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      description: Yup.string(),
      activity_type: Yup.string().required("Activity type is required"),
      start_date: Yup.date().nullable().required("Start date is required"),
      end_date: Yup.date()
        .nullable()
        .when("is_all_day", {
          is: false,
          then: Yup.date()
            .required("End date is required for timed activities")
            .min(Yup.ref("start_date"), "End date must be after start date"),
        }),
      is_all_day: Yup.boolean(),
      season: Yup.string().required("Season is required"),
      priority: Yup.string().required("Priority is required"),
      status: Yup.string().required("Status is required"),
      is_recurring: Yup.boolean(),
      recurrence_pattern: Yup.string().when("is_recurring", {
        is: true,
        then: Yup.string().required("Recurrence pattern is required"),
      }),
      recurrence_interval: Yup.number().when("is_recurring", {
        is: true,
        then: Yup.number()
          .min(1, "Must be at least 1")
          .required("Recurrence interval is required"),
      }),
      recurrence_end_date: Yup.date().when("is_recurring", {
        is: true,
        then: Yup.date()
          .required("Recurrence end date is required")
          .min(Yup.ref("start_date"), "Must be after start date"),
      }),
      template_id: Yup.number().nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmissionError(null);
      try {
        const formattedValues = {
          ...values,
          start_date: values.start_date
            ? format(values.start_date, "yyyy-MM-dd")
            : null,
          end_date: values.is_all_day
            ? null
            : values.end_date
            ? format(values.end_date, "yyyy-MM-dd")
            : null,
          recurrence_end_date: values.is_recurring
            ? values.recurrence_end_date
              ? format(values.recurrence_end_date, "yyyy-MM-dd")
              : null
            : null,
          description: values.description || null,
          template_id: values.template_id || null,
        };

        if (isSuperAdmin && farmTenantParam == null) {
          throw new Error("tenant_id is required in the URL for platform administrators");
        }
        if (isEdit) {
          await updateActivity(id, formattedValues, {
            tenantId: farmTenantParam,
          });
          enqueueSnackbar("Activity updated successfully!", {
            variant: "success",
          });
        } else {
          await createActivity(formattedValues, {
            tenantId: farmTenantParam,
          });
          enqueueSnackbar("Activity created successfully!", {
            variant: "success",
          });
        }

        navigate("/planning/activities");
      } catch (error) {
        console.error("Error saving activity:", error);
        setSubmissionError(error.message || "Failed to save activity");
        enqueueSnackbar(
          error.message || `Failed to ${isEdit ? "update" : "create"} activity`,
          { variant: "error" }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Load templates for template selection
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await getTemplates();
        setTemplates(data);
      } catch (error) {
        enqueueSnackbar("Failed to load templates", { variant: "error" });
      }
    };
    loadTemplates();
  }, [enqueueSnackbar]);

  // Load activity data for editing
  useEffect(() => {
    if (!isEdit) return;

    const loadActivity = async () => {
      try {
        const activity = await getActivityById(id, {
          tenantId: farmTenantParam,
        });
        formik.setValues({
          name: activity.name || "",
          description: activity.description || "",
          activity_type: activity.activity_type || "Crop",
          start_date: activity.start_date
            ? new Date(activity.start_date)
            : null,
          end_date: activity.end_date ? new Date(activity.end_date) : null,
          is_all_day: activity.is_all_day || true,
          season: activity.season || "Dry Season",
          priority: activity.priority || "Medium",
          status: activity.status || "Planned",
          is_recurring: activity.is_recurring || false,
          recurrence_pattern: activity.recurrence_pattern || "Daily",
          recurrence_interval: activity.recurrence_interval || 1,
          recurrence_end_date: activity.recurrence_end_date
            ? new Date(activity.recurrence_end_date)
            : null,
          template_id: activity.template_id || null,
        });
      } catch (error) {
        enqueueSnackbar("Failed to load activity data", { variant: "error" });
        navigate("/planning/activities");
      }
    };

    loadActivity();
  }, [id, isEdit, enqueueSnackbar, navigate, formik, farmTenantParam]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setTemplateDialogOpen(true);
  };

  const confirmTemplateUse = () => {
    if (!selectedTemplate) return;

    formik.setValues({
      ...formik.values,
      name: selectedTemplate.name,
      description: selectedTemplate.description,
      activity_type: selectedTemplate.activity_type,
      priority: selectedTemplate.default_priority,
      season: selectedTemplate.typical_season,
      is_recurring: selectedTemplate.is_recurring,
      recurrence_pattern: selectedTemplate.recurrence_pattern,
      template_id: selectedTemplate.id,
    });

    setTemplateDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !formik.values.start_date) return;

    try {
      const activity = await instantiateTemplate(
        selectedTemplate.id,
        format(formik.values.start_date, "yyyy-MM-dd"),
        { tenantId: farmTenantParam }
      );
      enqueueSnackbar("Activity created from template!", {
        variant: "success",
      });
      navigate(`/planning/activities/${activity.id}`);
    } catch (error) {
      enqueueSnackbar("Failed to create from template", { variant: "error" });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? "Edit Activity" : "Create New Activity"}
        </Typography>
        {isSuperAdmin && farmTenantParam == null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Add{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              ?tenant_id=(farm id)
            </Box>{" "}
            to this address bar before saving (required for platform
            administrators).
          </Alert>
        )}

        {!isEdit && templates.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Or create from template:
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {templates.slice(0, 3).map((template) => (
                <Button
                  key={template.id}
                  variant="outlined"
                  onClick={() => handleTemplateSelect(template)}
                >
                  {template.name}
                </Button>
              ))}
              {templates.length > 3 && (
                <Button
                  variant="text"
                  component={RouterLink}
                  to="/planning/templates"
                >
                  View all templates...
                </Button>
              )}
            </Box>
          </Box>
        )}

        {submissionError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submissionError}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Activity Name *"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Activity Type *</InputLabel>
                <Select
                  id="activity_type"
                  name="activity_type"
                  label="Activity Type *"
                  value={formik.values.activity_type}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.activity_type &&
                    Boolean(formik.errors.activity_type)
                  }
                >
                  {getActivityTypeOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>
                  {formik.touched.activity_type && formik.errors.activity_type}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Season *</InputLabel>
                <Select
                  id="season"
                  name="season"
                  label="Season *"
                  value={formik.values.season}
                  onChange={formik.handleChange}
                  error={formik.touched.season && Boolean(formik.errors.season)}
                >
                  {getSeasonOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>
                  {formik.touched.season && formik.errors.season}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Start Date *"
                value={formik.values.start_date}
                onChange={(date) => {
                  formik.setFieldValue("start_date", date);
                  // If it's an all-day activity with default duration, set end date
                  if (
                    formik.values.is_all_day &&
                    !formik.values.end_date &&
                    date
                  ) {
                    formik.setFieldValue("end_date", date);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={
                      formik.touched.start_date &&
                      Boolean(formik.errors.start_date)
                    }
                    helperText={
                      formik.touched.start_date && formik.errors.start_date
                    }
                  />
                )}
                inputFormat="yyyy-MM-dd"
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.is_all_day}
                    onChange={(e) => {
                      formik.setFieldValue("is_all_day", e.target.checked);
                      // If switching to all-day, set end date to start date
                      if (e.target.checked && formik.values.start_date) {
                        formik.setFieldValue(
                          "end_date",
                          formik.values.start_date
                        );
                      }
                    }}
                  />
                }
                label="All-day activity"
                sx={{ mb: 2 }}
              />

              {!formik.values.is_all_day && (
                <DatePicker
                  label="End Date *"
                  value={formik.values.end_date}
                  onChange={(date) => formik.setFieldValue("end_date", date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        formik.touched.end_date &&
                        Boolean(formik.errors.end_date)
                      }
                      helperText={
                        formik.touched.end_date && formik.errors.end_date
                      }
                    />
                  )}
                  inputFormat="yyyy-MM-dd"
                  minDate={formik.values.start_date}
                  sx={{ mb: 2 }}
                />
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority *</InputLabel>
                <Select
                  id="priority"
                  name="priority"
                  label="Priority *"
                  value={formik.values.priority}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.priority && Boolean(formik.errors.priority)
                  }
                >
                  {getPriorityOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>
                  {formik.touched.priority && formik.errors.priority}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status *</InputLabel>
                <Select
                  id="status"
                  name="status"
                  label="Status *"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  error={formik.touched.status && Boolean(formik.errors.status)}
                >
                  {getStatusOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error>
                  {formik.touched.status && formik.errors.status}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.is_recurring}
                    onChange={(e) =>
                      formik.setFieldValue("is_recurring", e.target.checked)
                    }
                  />
                }
                label="Recurring Activity"
              />
            </Grid>

            <Collapse in={formik.values.is_recurring} sx={{ width: "100%" }}>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Recurrence Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Recurrence Pattern *</InputLabel>
                  <Select
                    id="recurrence_pattern"
                    name="recurrence_pattern"
                    label="Recurrence Pattern *"
                    value={formik.values.recurrence_pattern}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.recurrence_pattern &&
                      Boolean(formik.errors.recurrence_pattern)
                    }
                  >
                    {getRecurrencePatternOptions().map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {formik.touched.recurrence_pattern &&
                      formik.errors.recurrence_pattern}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  id="recurrence_interval"
                  name="recurrence_interval"
                  label="Recurrence Interval *"
                  type="number"
                  value={formik.values.recurrence_interval}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.recurrence_interval &&
                    Boolean(formik.errors.recurrence_interval)
                  }
                  helperText={
                    formik.touched.recurrence_interval &&
                    formik.errors.recurrence_interval
                  }
                  sx={{ mb: 2 }}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Recurrence End Date *"
                  value={formik.values.recurrence_end_date}
                  onChange={(date) =>
                    formik.setFieldValue("recurrence_end_date", date)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        formik.touched.recurrence_end_date &&
                        Boolean(formik.errors.recurrence_end_date)
                      }
                      helperText={
                        formik.touched.recurrence_end_date &&
                        formik.errors.recurrence_end_date
                      }
                    />
                  )}
                  inputFormat="yyyy-MM-dd"
                  minDate={formik.values.start_date}
                />
              </Grid>
            </Collapse>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/planning/activities")}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                  fullWidth
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {formik.isSubmitting ? "Saving..." : "Save Activity"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Template Confirmation Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
      >
        <DialogTitle>Use Template</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <>
              <Typography>
                You've selected the "{selectedTemplate.name}" template.
              </Typography>
              <Typography sx={{ mt: 2 }}>Would you like to:</Typography>
              <ul>
                <li>
                  <Typography>
                    <strong>Prefill the form</strong> with template details (you
                    can modify before saving)
                  </Typography>
                </li>
                <li>
                  <Typography>
                    <strong>Instantiate directly</strong> (create activity
                    immediately using template defaults)
                  </Typography>
                </li>
              </ul>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmTemplateUse}
            color="primary"
            variant="outlined"
          >
            Prefill Form
          </Button>
          <Button
            onClick={handleCreateFromTemplate}
            color="primary"
            variant="contained"
            disabled={!formik.values.start_date}
          >
            Instantiate Directly
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityForm;
